import React from 'react';
import { gql, useQuery, useMutation } from '@apollo/client';
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Table, TableHead, TableRow, TableCell, TableBody, Checkbox, Typography } from '@material-ui/core';

const GET_RECOMMENDABLE_TEMPLATES = gql`
  query RecommendableTaskTemplates($sprint_id: ID!, $search: String, $limit: Int, $offset: Int) {
    recommendableTaskTemplates(sprint_id: $sprint_id, search: $search, limit: $limit, offset: $offset) {
      id
      key
      title
      description
      cepaCategory
      in_master_plan
    }
  }
`;

const RECOMMEND_TASKS = gql`
  mutation RecommendTasksToSprint($sprint_id: ID!, $template_ids: [ID!]!, $comment: String!, $due_at: ISO8601Date) {
    recommend_tasks_to_sprint(sprint_id: $sprint_id, template_ids: $template_ids, comment: $comment, due_at: $due_at) {
      errors
      sprint { id progress_percent_cached items { id status } }
    }
  }
`;

const RecommendTasksModal = ({ open, onClose, sprintId, onCompleted }) => {
  const [search, setSearch] = React.useState('');
  const [selected, setSelected] = React.useState([]);
  const [comment, setComment] = React.useState('');
  const [dueAt, setDueAt] = React.useState('');
  const { data, loading, refetch } = useQuery(GET_RECOMMENDABLE_TEMPLATES, { variables: { sprint_id: sprintId, search, limit: 20, offset: 0 }, skip: !open });
  const [recommend, { loading: recommending }] = useMutation(RECOMMEND_TASKS, { onCompleted: (res) => { onCompleted && onCompleted(res); onClose(); setSelected([]); setComment(''); } });

  const rows = data?.recommendableTaskTemplates || [];

  const toggle = (id) => setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const canSubmit = selected.length > 0 && comment.trim().length > 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Recommend Tasks</DialogTitle>
      <DialogContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <TextField value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search templates" size="small" variant="outlined" style={{ minWidth: 260 }} onKeyDown={(e)=>{ if(e.key==='Enter'){ refetch({ sprint_id: sprintId, search, limit: 20, offset: 0 }); } }} />
          <Typography variant="body2">{selected.length} selected</Typography>
        </Box>
        <Box display="flex" alignItems="center" gridGap={16} mb={2}>
          <TextField
            label="Due date (optional)"
            type="date"
            size="small"
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            value={dueAt}
            onChange={(e)=> setDueAt(e.target.value)}
          />
          <Typography variant="caption" color="textSecondary">Applied to all selected tasks</Typography>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox"></TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>In Master Plan?</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5}><Typography>Loading…</Typography></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={5}><Typography color="textSecondary">No templates found.</Typography></TableCell></TableRow>
            ) : rows.map((t) => (
              <TableRow key={t.id} hover>
                <TableCell padding="checkbox"><Checkbox checked={selected.includes(t.id)} onChange={()=>toggle(t.id)} /></TableCell>
                <TableCell>{t.title}</TableCell>
                <TableCell><Typography variant="body2" color="textSecondary">{t.description}</Typography></TableCell>
                <TableCell>{t.cepaCategory}</TableCell>
                <TableCell>{t.in_master_plan ? 'Yes' : 'No'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Box mt={2}>
          <TextField label="Comment (required)" value={comment} onChange={(e)=>setComment(e.target.value)} fullWidth multiline rows={3} variant="outlined" />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button color="primary" variant="contained" disabled={!canSubmit || recommending} onClick={() => recommend({ variables: { sprint_id: sprintId, template_ids: selected, comment, due_at: dueAt || null } })}>Recommend</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecommendTasksModal;


